import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { detectRegion } from '../lib/regions';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check active sessions and sets the user
        const getInitialSession = async () => {
            try {
                const { data: { session }, error } = await supabase.auth.getSession();
                if (error) throw error;

                setUser(session?.user ?? null);
                if (session?.user) {
                    await fetchProfile(session.user.id);
                }
            } catch (err) {
                const isAbort = err?.name === 'AbortError' || err?.message?.includes('aborted');
                if (!isAbort) {
                    console.error('Error in getInitialSession:', err);
                }
            } finally {
                setLoading(false);
            }
        };

        getInitialSession();

        // Listen for changes on auth state (sign in, sign out, etc.)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log('Auth event:', event, session?.user?.id);
            const currentUser = session?.user ?? null;
            setUser(currentUser);

            if (currentUser) {
                const existingProfile = await fetchProfile(currentUser.id);

                // If user logged in (especially via Social) but has no profile, create one
                if (!existingProfile && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
                    console.log('No profile found for signed-in user, creating default...');
                    await createProfile(
                        currentUser,
                        currentUser.user_metadata?.full_name || currentUser.email?.split('@')[0] || 'User'
                    );
                }
            } else {
                setProfile(null);
            }
            setLoading(false);
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    const fetchProfile = async (userId) => {
        try {
            // Add a timeout fallback for the profile fetch
            const profilePromise = supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            const { data, error } = await Promise.race([
                profilePromise,
                new Promise((_, reject) => setTimeout(() => reject(new Error('Profile fetch timeout')), 5000))
            ]);

            if (error && error.code === 'PGRST116') {
                return null;
            }

            if (data) {
                setProfile(data);
                if (!data.pricing_region) {
                    detectRegion().then(reg => updateProfile({ pricing_region: reg }));
                }
            }
            return data;
        } catch (err) {
            console.error('Error in fetchProfile:', err);
            return null;
        }
    };

    const createProfile = async (user, fullName, regionChoice) => {
        try {
            const region = regionChoice || await detectRegion();
            const expiresAt = new Date();
            expiresAt.setMonth(expiresAt.getMonth() + 2);

            const profileData = {
                id: user.id,
                full_name: fullName,
                subscription_tier: 'Free',
                pricing_region: region,
                free_tier_expires_at: expiresAt.toISOString(),
                is_verified: true,
            };

            const { data, error } = await supabase
                .from('profiles')
                .insert([profileData])
                .select()
                .single();

            if (error) {
                // If it's a conflict or already exists (code 23505), just fetch the existing one quietly
                if (error.code === '23505') {
                    const existing = await fetchProfile(user.id);
                    return { data: existing, error: null };
                }

                console.error('Error creating profile:', error.message || error);
                return { data: null, error };
            }

            setProfile(data);
            return { data, error: null };
        } catch (err) {
            console.error('Catch error in createProfile:', err);
            return { data: null, error: err };
        }
    };

    const isSubscriptionActive = () => {
        if (!profile) return false;
        if (profile.subscription_tier === 'Premium') return true;

        const expiryDate = new Date(profile.free_tier_expires_at);
        return expiryDate > new Date();
    };

    const daysUntilExpiry = () => {
        if (!profile || profile.subscription_tier === 'Premium') return null;
        const expiryDate = new Date(profile.free_tier_expires_at);
        const today = new Date();
        const diffTime = expiryDate - today;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const updateProfile = async (updates) => {
        if (!user) return { error: 'No user session' };

        try {
            const { data, error } = await supabase
                .from('profiles')
                .update(updates)
                .eq('id', user.id)
                .select()
                .single();

            if (error) {
                // If it's a "column not found" error, don't crash the app
                if (error.code === 'PGRST204') {
                    console.warn('Profile update failed: Column not found in DB. This is expected if the schema is not yet updated.');
                    return { data: updates, error: null }; // Treat as success to avoid UI errors
                }
                console.error('Error updating profile:', error);
                return { data: null, error };
            }

            setProfile(data);
            return { data, error: null };
        } catch (err) {
            console.error('Catch error in updateProfile:', err);
            return { data: null, error: err };
        }
    };

    const value = {
        user,
        profile,
        loading,
        createProfile,
        updateProfile,
        isSubscriptionActive,
        daysUntilExpiry,
        signOut: async () => {
            await supabase.auth.signOut();
            setProfile(null);
            setUser(null);
            window.location.href = '/login';
        },
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
